/*******************************************************************************
 * @ngdoc    object
 * @module   csm.books.InitialSetup.services
 * @name     csm.books.InitialSetup.services.Data.type:SetupAccount
 * @constructor
 * @param {object=} data  Properties to set.
 * @description
 * Account Information for Initial Setup.
 *******************************************************************************/
/* @ngInject */ export function setupAccountConfig(TypeFactoryProvider) {
  TypeFactoryProvider.addType({
    name: 'Data.SetupAccount',
    inherits: 'Data.books.accounts.AccountAdminInfo',
    $type: 'csm.books.data.setup.SetupAccount, CSMBooks',
    prototype: function ($injector) {
      return Object.defineProperties(
        {
          userAdded: false, // true=User added account, false=default account.
          fyBalance: 0, // Fiscal year forward balance for BS accounts, current balance for IE accounts.
          tyBalance: 0 // Tax year forward balance for BS accounts, current balance for IE accounts.
        },
        /*--------------*/
        /* Client side. */
        /*--------------*/
        {
          fyCurrentBalance: $injector.invoke(fyCurrentBalance),
          fyOpeningBalance: $injector.invoke(fyOpeningBalance),
          isBankAccount: $injector.invoke(isBankAccount),
          isBSAccount: $injector.invoke(isBSAccount),
          isDebitAccount: $injector.invoke(isDebitAccount),
          isFundBalance: $injector.invoke(isFundBalance),
          isIEAccount: $injector.invoke(isIEAccount),
          tyCurrentBalance: $injector.invoke(tyCurrentBalance),
          tyOpeningBalance: $injector.invoke(tyOpeningBalance)
        }
      );
    },
    constructor: function (Data) {
      return function (data) {
        Data.books.accounts.AccountAdminInfo.call(this, data);
      };
    }
  });
};

/***************************************************************************
 * Property: fyCurrentBalance */
/**
 * @ngdoc      property
 * @name       fyCurrentBalance
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Current balance for Fiscal Year period. Used primarily in Balances Panel.
 * Gets/sets `fyBalance` for Income/Expense accounts. Getter return
 * `currentBalance` for Balance Sheet accounts.
 ***************************************************************************/
/* @ngInject */ function fyCurrentBalance() {
  return {
    get: function () {
      return this.isIEAccount ? this.fyBalance : this.currentBalance;
    },
    set: function (v) {
      if (this.isIEAccount) this.fyBalance = v;
      else
        throw "'fyCurrentBalance' can't be set for Balance Sheet accounts.";
    }
  };
}

/***************************************************************************
 * Property: fyOpeningBalance */
/**
 * @ngdoc      property
 * @name       fyOpeningBalance
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Opening balance for Fiscal Year period. Used primarily in Balances Panel.
 * Gets/sets `fyBalance` for Balance Sheet accounts. Getter returns 0 for
 * Income/Expense accounts.
 ***************************************************************************/
/* @ngInject */ function fyOpeningBalance() {
  return {
    get: function () {
      return this.isIEAccount ? 0 : this.fyBalance;
    },
    set: function (v) {
      if (!this.isIEAccount) this.fyBalance = v;
      else
        throw "'fyOpeningBalance' can't be set for Income/Expense accounts.";
    }
  };
}

/***************************************************************************
 * Property: isBankAccount */
/**
 * @ngdoc      property
 * @name       isBankAccount
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Returns `true` if this is the Bank account.
 ***************************************************************************/
/* @ngInject */ function isBankAccount(SERVER) {
  return {
    get: function () {
      return this.type === SERVER.AccountTypes.BANK;
    }
  };
}

/***************************************************************************
 * Property: isBSAccount */
/**
 * @ngdoc      property
 * @name       isBSAccount
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Returns `true` if this is a Bank Statement account. This is all accounts
 * that are not Income, Expense, or Fund Balance accounts.
 ***************************************************************************/
/* @ngInject */ function isBSAccount() {
  return {
    get: function () {
      return !this.isIEAccount && !this.isFundBalance;
    }
  };
}

/***************************************************************************
 * Property: isDebitAccount */
/**
 * @ngdoc      property
 * @name       isDebitAccount
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Returns `true` if this is a Debit balance account.
 ***************************************************************************/
/* @ngInject */ function isDebitAccount(SERVER) {
  return {
    get: function () {
      return this.side === SERVER.BooksDBConstants.AS_DEBIT;
    }
  };
}

/***************************************************************************
 * Property: isFundBalance */
/**
 * @ngdoc      property
 * @name       isFundBalance
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Returns `true` if this is the Fund Balance account.
 ***************************************************************************/
/* @ngInject */ function isFundBalance(SERVER) {
  return {
    get: function () {
      return this.type === SERVER.AccountTypes.FUND_BALANCE;
    }
  };
}

/***************************************************************************
 * Property: isIEAccount */
/**
 * @ngdoc      property
 * @name       isIEAccount
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Returns `true` if this is an Income or Expense account.
 ***************************************************************************/
/* @ngInject */ function isIEAccount(SERVER) {
  var ieTypes = [
    SERVER.BooksDBConstants.AC_REVENUE,
    SERVER.BooksDBConstants.AC_EXPENSE
  ];

  return {
    get: function () {
      return ieTypes.indexOf(this.accountClass) >= 0;
    }
  };
}

/***************************************************************************
 * Property: tyCurrentBalance */
/**
 * @ngdoc      property
 * @name       tyCurrentBalance
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Current balance for Tax Year period. Used primarily in Balances Panel.
 * Gets/sets `tyBalance` for Income/Expense accounts. Getter return
 * `currentBalance` for Balance Sheet accounts.
 ***************************************************************************/
/* @ngInject */ function tyCurrentBalance() {
  return {
    get: function () {
      return this.isIEAccount ? this.tyBalance : this.currentBalance;
    },
    set: function (v) {
      if (this.isIEAccount) this.tyBalance = v;
      else
        throw "'tyCurrentBalance' can't be set for Balance Sheet accounts.";
    }
  };
}

/***************************************************************************
 * Property: tyOpeningBalance */
/**
 * @ngdoc      property
 * @name       tyOpeningBalance
 * @propertyOf csm.books.InitialSetup.services.Data.type:SetupAccount
 * @description
 * Opening balance for Tax Year period. Used primarily in Balances Panel.
 * Gets/sets `tyBalance` for Balance Sheet accounts. Getter returns 0 for
 * Income/Expense accounts.
 ***************************************************************************/
/* @ngInject */ function tyOpeningBalance() {
  return {
    get: function () {
      return this.isIEAccount ? 0 : this.tyBalance;
    },
    set: function (v) {
      if (!this.isIEAccount) this.tyBalance = v;
      else
        throw "'tyOpeningBalance' can't be set for Income/Expense accounts.";
    }
  };
}
